import { useMutation } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";

import { apiClient } from "~/api/ApiClient";

export type StripeSessionType = "checkout" | "setup" | "pay";

interface StripeSessionError {
  error?: {
    error?: {
      message?: string;
    };
  };
}

interface UseStripeSessionProps {
  sessionType: StripeSessionType;
  onSuccess?: (clientSecret: string) => void;
  onError?: (error: StripeSessionError) => void;
}

export function useStripeSession({
  sessionType,
  onSuccess,
  onError,
}: UseStripeSessionProps) {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const getSessionEndpoint = () => {
    switch (sessionType) {
      case "checkout":
        return () =>
          apiClient.billing.sessionCheckoutCreate({
            ui_mode: "embedded",
          });
      case "setup":
        return () =>
          apiClient.billing.sessionSetupCreate({
            ui_mode: "embedded",
          });
      case "pay":
        return () =>
          apiClient.billing.sessionPayCreate({
            ui_mode: "embedded",
          });
      default:
        throw new Error(`Unknown session type: ${sessionType}`);
    }
  };

  const { mutate: createSession, isPending: isCreatingSession } = useMutation({
    mutationFn: async () => {
      const endpoint = getSessionEndpoint();

      return await endpoint();
    },
    onSuccess: async (response) => {
      const secret = response?.data?.["payments/session"].client_secret;
      if (secret) {
        setClientSecret(secret);
        setIsModalOpen(true);
        onSuccess?.(secret);
      }
    },
    onError: (error: StripeSessionError) => {
      console.error(`Failed to create ${sessionType} session:`, error);
      const errorMessage =
        error?.error?.error?.message ||
        `Failed to create ${sessionType} session`;
      enqueueSnackbar(`Error: ${errorMessage}`, {
        variant: "error",
        persist: true,
      });
      onError?.(error);
    },
  });

  // Close the modal after a completed payment — the session is consumed,
  // so unlike closeModal there is nothing to cancel server-side.
  const finishModal = () => {
    setIsModalOpen(false);
    setClientSecret(null);
  };

  const closeModal = async () => {
    if (clientSecret) {
      try {
        await apiClient.billing.sessionCancelCreate();
      } catch (error) {
        console.error("Failed to cancel Stripe session:", error);
      }
    }

    finishModal();
  };

  const openSession = () => {
    createSession();
  };

  return {
    clientSecret,
    isModalOpen,
    isCreatingSession,
    openSession,
    closeModal,
    finishModal,
    createSession,
    setOpen: setIsModalOpen,
    handleClose: closeModal,
  };
}
